import { createAuthClient } from "better-auth/react";
import { passkeyClient } from "@better-auth/passkey/client";
import { twoFactorClient } from "better-auth/client/plugins";

export const authClient = createAuthClient({
  baseURL: typeof window === "undefined" ? "" : window.location.origin,
  plugins: [passkeyClient(), twoFactorClient()],
});

export const { signIn, signOut, useSession } = authClient;

export type SessionUser = {
  id: string;
  email: string;
  isAdmin: boolean;
  isActive: boolean;
  firstName: string | null;
  lastName: string | null;
};

export async function fetchMe(): Promise<SessionUser | null> {
  const res = await fetch("/api/me", { credentials: "include" });
  if (res.status === 401) {
    const body = (await res.json().catch(() => ({}))) as { code?: string };
    if (body.code === "LOCKED") {
      // Session is valid, just locked. Throw so React Query keeps the
      // previously cached user in `data` — losing it would knock out
      // SessionLockController's `authed` check and the overlay would
      // disappear, leaving the page rendered with no user behind it.
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("cerebellum:locked"));
      }
      throw new Error("LOCKED");
    }
    // Plain 401 — server-side session is gone. Tell the rest of the
    // app so any stale lock flag clears and AuthGuard kicks the user
    // to /login.
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("cerebellum:auth-expired"));
    }
    return null;
  }
  if (!res.ok) return null;
  const body = (await res.json()) as { user: SessionUser };
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("cerebellum:unlocked"));
  }
  return body.user;
}

// Read `?next=` from the current URL and validate it's a same-origin
// internal path before handing it to a router. Defends against an
// attacker stuffing `next=https://evil.com` into a /login link.
export function getPostLoginRedirect(): string {
  if (typeof window === "undefined") return "/";
  const next = new URLSearchParams(window.location.search).get("next");
  if (!next) return "/";
  if (!next.startsWith("/")) return "/";
  if (next.startsWith("//") || next.startsWith("/\\")) return "/";
  return next;
}
